import { useEffect, useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { api, adminApi } from "@/lib/api";

const OPEN_START = 480; // 08:00
const OPEN_END = 1200; // 20:00
const STEP = 30; // grille 30 min
const BUFFER = 15; // transition 15 min
const CELL_H = 26; // hauteur px par cellule 30 min (vue semaine)

export const KIND_COLORS = {
    abonnement: { block: "bg-blue-500/20 border-blue-400/60 text-blue-200", dot: "bg-blue-400", label: "Abonnement" },
    decouverte: { block: "bg-green-500/20 border-green-400/60 text-green-200", dot: "bg-green-400", label: "Découverte" },
    premium: { block: "bg-violet-500/20 border-violet-400/60 text-violet-200", dot: "bg-violet-400", label: "Premium" },
    pro: { block: "bg-violet-500/20 border-violet-400/60 text-violet-200", dot: "bg-violet-400", label: "Premium" },
};

const hm = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const parseHM = (s) => {
    const [h, m] = s.split(":").map(Number);
    return h * 60 + m;
};
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromISO = (iso) => new Date(`${iso}T12:00:00`);
const addDays = (iso, n) => {
    const d = fromISO(iso);
    d.setDate(d.getDate() + n);
    return toISO(d);
};
const mondayOf = (iso) => {
    const d = fromISO(iso);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return toISO(d);
};
const todayISO = () => toISO(new Date());

const DAY_SHORT = ["lun.", "mar.", "mer.", "jeu.", "ven.", "sam.", "dim."];
const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

function fmtDay(iso) {
    const d = fromISO(iso);
    return `${DAY_SHORT[(d.getDay() + 6) % 7]} ${d.getDate()}`;
}

export default function BookingCalendar({ duration, value, onChange, adminMode = false, readOnly = false, onVisibleRangeChange }) {
    const [view, setView] = useState(() => (typeof window !== "undefined" && window.innerWidth < 640 ? "day" : "week"));
    const [cursor, setCursor] = useState(todayISO());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const range = useMemo(() => {
        if (view === "day") return { start: cursor, days: 1 };
        if (view === "week") return { start: mondayOf(cursor), days: 7 };
        const firstOfMonth = `${cursor.slice(0, 8)}01`;
        return { start: mondayOf(firstOfMonth), days: 42 };
    }, [view, cursor]);

    useEffect(() => {
        onVisibleRangeChange?.(range.start);
        let cancelled = false;
        setLoading(true);
        const client = adminMode ? adminApi : api;
        client
            .get(adminMode ? `/admin/calendar?start=${range.start}&days=${range.days}` : `/calendar?start=${range.start}&days=${range.days}`)
            .then((r) => {
                if (!cancelled) setData(r.data);
            })
            .catch(() => {
                if (!cancelled) setData(null);
            })
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [range, adminMode]);

    const byDate = useMemo(() => {
        const map = {};
        (data?.days || []).forEach((d) => {
            map[d.date] = d.bookings;
        });
        return map;
    }, [data]);

    const cellStateAt = (dateIso, t) => {
        if (dateIso < todayISO()) return "past";
        if (dateIso === todayISO()) {
            const now = new Date();
            if (t <= now.getHours() * 60 + now.getMinutes()) return "past";
        }
        for (const b of byDate[dateIso] || []) {
            const s = parseHM(b.start_time);
            const e = s + b.duration_minutes;
            if (t >= s && t < e) return "booked";
            if (t >= e && t < e + BUFFER) return "buffer";
        }
        return "free";
    };

    const fits = (dateIso, t) => {
        if (!duration) return false;
        if (t < OPEN_START || t + duration > OPEN_END) return false;
        for (let m = t; m < t + duration; m += STEP) {
            if (cellStateAt(dateIso, m) !== "free") return false;
        }
        // la réservation suivante doit laisser 15 min de transition
        if (t + duration < OPEN_END && cellStateAt(dateIso, t + duration) === "booked") return false;
        return true;
    };

    const isSelectedCell = (dateIso, t) => {
        if (!value?.date || !value?.time_slot || value.date !== dateIso) return false;
        const s = parseHM(value.time_slot);
        return t >= s && t < s + (duration || 0);
    };

    const select = (dateIso, t) => {
        if (readOnly || !fits(dateIso, t)) return;
        onChange({ date: dateIso, time_slot: hm(t) });
    };

    const nav = (dir) => {
        const stepDays = view === "day" ? 1 : view === "week" ? 7 : 28;
        let next = addDays(cursor, dir * stepDays);
        if (view === "month") {
            const d = fromISO(cursor);
            d.setDate(1);
            d.setMonth(d.getMonth() + dir);
            next = toISO(d);
        }
        setCursor(next);
    };

    const title =
        view === "day"
            ? `${fmtDay(cursor)} ${MONTHS[fromISO(cursor).getMonth()]}`
            : view === "week"
            ? `Semaine du ${fmtDay(range.start)}`
            : `${MONTHS[fromISO(cursor).getMonth()]} ${fromISO(cursor).getFullYear()}`;

    const weekMinLeft = data ? Math.max(0, data.week.commercial_cap_min - data.week.commercial_used_min) : null;

    const renderDayColumn = (dateIso, wide = false) => {
        const bookings = byDate[dateIso] || [];
        const cellH = wide ? 40 : CELL_H;
        const cells = [];
        for (let t = OPEN_START; t < OPEN_END; t += STEP) cells.push(t);
        return (
            <div key={dateIso} className="relative flex-1 min-w-[100px] border-l border-white/5 first:border-l-0" data-testid={`cal-day-${dateIso}`}>
                {cells.map((t) => {
                    const st = cellStateAt(dateIso, t);
                    const sel = isSelectedCell(dateIso, t);
                    const clickable = !readOnly && st === "free" && fits(dateIso, t);
                    return (
                        <button
                            key={t}
                            type="button"
                            disabled={!clickable}
                            onClick={() => select(dateIso, t)}
                            style={{ height: cellH }}
                            title={`${hm(t)} — ${st === "free" ? (clickable ? "Disponible" : "Trop court pour cette durée") : st === "booked" ? "Réservé" : st === "buffer" ? "Transition 15 min" : "Indisponible"}`}
                            className={`w-full border-b border-white/5 transition-colors ${
                                sel
                                    ? "bg-amber-400/40"
                                    : st === "free"
                                    ? clickable
                                        ? "bg-emerald-400/[0.04] hover:bg-amber-300/20 cursor-pointer"
                                        : "bg-white/[0.01] cursor-not-allowed"
                                    : st === "buffer"
                                    ? "cursor-not-allowed"
                                    : "cursor-not-allowed"
                            }`}
                            data-testid={clickable ? `cal-slot-${dateIso}-${hm(t)}` : undefined}
                        >
                            {st === "buffer" && (
                                <span
                                    className="block w-full h-full opacity-40"
                                    style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(113,113,122,0.5) 3px, rgba(113,113,122,0.5) 5px)" }}
                                />
                            )}
                        </button>
                    );
                })}
                {bookings.map((b, i) => {
                    const s = parseHM(b.start_time);
                    const e = s + b.duration_minutes;
                    const c = KIND_COLORS[b.package_id] || KIND_COLORS.decouverte;
                    const top = ((s - OPEN_START) / STEP) * cellH;
                    const height = (b.duration_minutes / STEP) * cellH;
                    return (
                        <div
                            key={i}
                            className={`absolute left-0.5 right-0.5 rounded-md border px-1.5 py-0.5 overflow-hidden pointer-events-none ${c.block}`}
                            style={{ top: top + 1, height: height - 2 }}
                            data-testid={`cal-booking-${dateIso}-${b.start_time}`}
                        >
                            <p className="text-[10px] font-medium leading-tight truncate">
                                {hm(s)}–{hm(e)} · {c.label}
                            </p>
                            {(wide || b.duration_minutes >= 120) && (
                                <p className="text-[9px] opacity-80 leading-tight truncate">
                                    {b.duration_minutes / 60}h · {b.price > 0 ? `${b.price}€` : "inclus"}
                                    {adminMode && b.client ? ` · ${b.client}` : ""}
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const timeGutter = (cellH) => {
        const cells = [];
        for (let t = OPEN_START; t < OPEN_END; t += STEP) cells.push(t);
        return (
            <div className="w-12 shrink-0">
                {cells.map((t) => (
                    <div key={t} style={{ height: cellH }} className="text-[9px] text-zinc-600 font-body text-right pr-2 -mt-1.5">
                        {t % 60 === 0 ? hm(t) : ""}
                    </div>
                ))}
            </div>
        );
    };

    const renderMonth = () => {
        const days = data?.days || [];
        return (
            <div className="grid grid-cols-7 gap-1.5" data-testid="cal-month-grid">
                {DAY_SHORT.map((d) => (
                    <div key={d} className="text-center text-[10px] uppercase tracking-widest text-zinc-500 pb-1">{d}</div>
                ))}
                {days.map((d) => {
                    const inMonth = d.date.slice(5, 7) === cursor.slice(5, 7);
                    const past = d.date < todayISO();
                    const busyMin = d.bookings.reduce((sum, b) => sum + b.duration_minutes + BUFFER, 0);
                    const freeMin = Math.max(0, OPEN_END - OPEN_START - busyMin);
                    const full = freeMin < (duration || 60) + BUFFER;
                    return (
                        <button
                            key={d.date}
                            type="button"
                            onClick={() => {
                                setCursor(d.date);
                                setView("day");
                            }}
                            className={`rounded-lg border p-2 min-h-[72px] text-left transition-colors ${
                                past
                                    ? "border-white/5 opacity-30"
                                    : value?.date === d.date
                                    ? "border-amber-400 bg-amber-400/10"
                                    : "border-white/10 bg-white/[0.02] hover:border-amber-300/40"
                            }`}
                            data-testid={`cal-month-day-${d.date}`}
                        >
                            <p className={`text-xs font-body ${inMonth ? "text-white" : "text-zinc-600"}`}>{fromISO(d.date).getDate()}</p>
                            <div className="mt-1 flex flex-wrap gap-1">
                                {d.bookings.slice(0, 4).map((b, i) => (
                                    <span key={i} className={`w-1.5 h-1.5 rounded-full ${(KIND_COLORS[b.package_id] || KIND_COLORS.decouverte).dot}`} />
                                ))}
                            </div>
                            {!past && (
                                <p className={`mt-1 text-[9px] font-body ${full ? "text-zinc-600" : "text-emerald-300/80"}`}>
                                    {d.bookings.length === 0 ? "Libre" : full ? "Complet" : `${Math.floor(freeMin / 60)}h libres`}
                                </p>
                            )}
                        </button>
                    );
                })}
            </div>
        );
    };

    return (
        <div data-testid="booking-calendar">
            {/* En-tête : navigation + vues */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <button type="button" onClick={() => nav(-1)} className="w-8 h-8 rounded-full border border-white/15 text-zinc-300 hover:border-amber-300/50 flex items-center justify-center" data-testid="cal-prev">
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => setCursor(todayISO())} className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-zinc-300 hover:border-amber-300/50 font-body" data-testid="cal-today">
                        Aujourd'hui
                    </button>
                    <button type="button" onClick={() => nav(1)} className="w-8 h-8 rounded-full border border-white/15 text-zinc-300 hover:border-amber-300/50 flex items-center justify-center" data-testid="cal-next">
                        <ChevronRight className="w-4 h-4" />
                    </button>
                    <span className="ml-2 font-display text-lg text-white">{title}</span>
                </div>
                <div className="flex rounded-full border border-white/10 overflow-hidden">
                    {[
                        ["day", "Jour"],
                        ["week", "Semaine"],
                        ["month", "Mois"],
                    ].map(([v, label]) => (
                        <button
                            key={v}
                            type="button"
                            onClick={() => setView(v)}
                            className={`px-4 py-1.5 text-xs font-body transition-colors ${view === v ? "bg-amber-400 text-black" : "text-zinc-400 hover:text-white"}`}
                            data-testid={`cal-view-${v}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Légende */}
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[10px] font-body text-zinc-400" data-testid="cal-legend">
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-blue-400" /> Abonnement</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-green-400" /> Découverte 1h · 90€</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-violet-400" /> Premium 6h · 620€</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm border border-emerald-300/50 bg-emerald-400/10" /> Libre</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(113,113,122,0.7) 2px, rgba(113,113,122,0.7) 4px)" }} /> Transition 15 min</span>
            </div>

            {loading && (
                <p className="mt-6 flex items-center gap-2 text-sm text-zinc-500 font-body">
                    <Loader2 className="w-4 h-4 animate-spin" /> Chargement des disponibilités…
                </p>
            )}

            {/* Vues */}
            <div className="mt-4">
                {view === "month" ? (
                    renderMonth()
                ) : (
                    <div className="overflow-x-auto rounded-xl border border-white/10 bg-black/30" data-testid="cal-grid">
                        <div className={view === "week" ? "min-w-[780px]" : "min-w-[280px]"}>
                            <div className="flex border-b border-white/10">
                                <div className="w-12 shrink-0" />
                                {(view === "week" ? (data?.days || []).map((d) => d.date) : [cursor]).map((dateIso) => (
                                    <div key={dateIso} className={`flex-1 min-w-[100px] py-2 text-center text-xs font-body border-l border-white/5 first:border-l-0 ${dateIso === todayISO() ? "text-amber-300" : "text-zinc-400"}`}>
                                        {fmtDay(dateIso)}
                                    </div>
                                ))}
                            </div>
                            <div className="flex">
                                {timeGutter(view === "day" ? 40 : CELL_H)}
                                {(view === "week" ? (data?.days || []).map((d) => d.date) : [cursor]).map((dateIso) => renderDayColumn(dateIso, view === "day"))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {weekMinLeft !== null && (
                <p className="mt-3 text-[11px] text-zinc-500 font-body">
                    {Math.floor(weekMinLeft / 60)}h de créneaux commerciaux restantes cette semaine — le reste est garanti aux abonnés.
                </p>
            )}

            {/* Sélection courante */}
            {value?.date && value?.time_slot && (
                <p className="mt-4 inline-flex items-center gap-2 rounded-md border border-amber-300/40 bg-amber-300/[0.08] px-3 py-2 text-sm text-amber-200 font-body" data-testid="cal-selection-summary">
                    <CalendarDays className="w-4 h-4" />
                    {fmtDay(value.date)} {MONTHS[fromISO(value.date).getMonth()]} · {value.time_slot} → {hm(parseHM(value.time_slot) + (duration || 0))}
                    {duration ? ` (${duration / 60}h)` : ""}
                </p>
            )}
        </div>
    );
}
